import { zodResolver } from "@hookform/resolvers/zod";
import Link from "next/link";
import { useRouter } from "next/router";
import { signIn } from "next-auth/react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Logo } from "~/components/icons/logo";
import { useUnauthenticated } from "~/hooks/use-unauthenticated";
import { cn } from "~/utils/cn";

export default function SignInPage() {
  useUnauthenticated();

  const router = useRouter();

  // Set up form
  type FormValues = {
    email: string;
    password: string;
  };
  const {
    register,
    handleSubmit,
    setError,
    formState: { isDirty, isSubmitting, errors },
  } = useForm<FormValues>({
    resolver: zodResolver(
      z.object({
        email: z.string().email(),
        password: z.string().min(8),
      })
    ),
  });

  // Submit
  const onSubmit = handleSubmit(async (data) => {
    const result = await signIn("credentials", {
      redirect: false,
      email: data.email,
      password: data.password,
    });

    if (result) {
      if (result.ok) {
        await router.push("/planner");
      } else {
        setError("root", {
          message:
            "Bad luck. Those details don't look correct. Try again or reset your password.",
        });
      }
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <div className="mx-[10px] sm:mx-auto my-10 max-w-sm">
        <div className="flex flex-col items-center">
          <Logo />
          <h1 className="mt-10 mb-4 text-4xl font-semibold">Log in</h1>

          {errors.root?.message && (
            <p className="mt-4 w-full text-center text-red-500">
              {errors.root.message}
            </p>
          )}

          <input
            {...register("email")}
            type="email"
            className={cn(
              "mt-4 block w-full rounded-md border-0 py-2 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#FC7569] sm:leading-6",
              errors.email && "ring-2 ring-red-500"
            )}
            placeholder="Enter your email address"
          />
          {errors.email?.message && (
            <p className="mt-2 w-full text-red-500">{errors.email.message}</p>
          )}

          <input
            {...register("password")}
            type="password"
            className={cn(
              "mt-4 block w-full rounded-md border-0 py-2 px-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#FC7569] sm:leading-6",
              errors.password && "ring-2 ring-red-500"
            )}
            placeholder="Enter your password"
          />
          {errors.password?.message && (
            <p className="mt-2 w-full text-red-500">
              {errors.password.message}
            </p>
          )}

          <button
            type="submit"
            disabled={!isDirty || isSubmitting}
            className="mt-4 block w-full rounded-md bg-[#FC7569] py-2 px-4 font-bold text-white shadow-sm hover:bg-[#F56C5E] disabled:bg-gray-100 disabled:text-gray-400 sm:leading-6"
          >
            Let me in!
          </button>

          <Link href="/forgot-password" className="mt-8 text-gray-500">
            Forgot password?
          </Link>
        </div>
      </div>
    </form>
  );
}
